import React from 'react';
import axios from 'axios';
import { isEqual } from 'lodash';
import errorReporter from '@app/errorReporter';
import { InputNumber, Select, Button, Drawer, SearchBar } from 'components';
import { Button as ButtonV2 } from '@bb/elements-web';
import { featureEnabled, sortByProperty } from 'utils';
import {
  AgentsContext,
  AgentsContextType,
  LayoutContext,
  ListingLabelContext,
} from 'contexts';
import { useBreakpoint, useDebounce } from 'hooks';
import { ListingLabel } from '@bb/automaton';
import { ListingsSearchOptionsType, StatusType } from './types';
import { StyledOptionsSection, StyledDownloadApply } from './StyledComponents';
import MyBrokerageContext from './MyBrokerageContext';
import PrintButton from './PrintButton';
import LockButton from './LockButton';

const ListingsSearchOptions: React.FC = () => {
  const {
    isAdvancedSearchOpen: isOpen,
    setAdvancedSearchOpen: setIsOpen,
    advancedSearchHeight: height,
    loading,
    locations,
    queryData,
    setQueryData,
    initialQueryData,
    filtersLocked,
    clearSearchOptions,
  } = React.useContext(MyBrokerageContext);
  const { md } = useBreakpoint();
  const { fetchOrgUsers } = React.useContext(AgentsContext);
  const { transition } = React.useContext(LayoutContext);
  const { getLabel, getDefaultLabel, getAvailability, isHidden } =
    React.useContext(ListingLabelContext);
  const [availabilities, setAvailabilities] = React.useState<StatusType[]>([]);
  const [agents, setAgents] = React.useState<AgentsContextType['orgUsers']>([]);
  const [searchOptions, setSearchOptions] = React.useState<
    typeof queryData.searchOptions
  >(() => {
    const hasCustomFilters = !isEqual(
      queryData.searchOptions,
      initialQueryData.searchOptions,
    );
    if (hasCustomFilters) return queryData.searchOptions;
    return { ...queryData.searchOptions, avail: 'All' };
  });
  const [localSearchTerm, setLocalSearchTerm] = React.useState(
    queryData.searchTerm,
  );
  const debouncedTerm = useDebounce(localSearchTerm, 500);
  const componentDidMount = React.useRef(false);

  const handleChange = (changes: Partial<ListingsSearchOptionsType>) => {
    setSearchOptions(prevState => ({ ...prevState, ...changes }));
  };

  const getAvailabilities = async () => {
    try {
      const { data } = await axios.get<StatusType[]>('/statuses');
      setAvailabilities(data);
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const hasSubBrokerage = () => {
    const { location } = searchOptions;
    if (
      locations.length === 0 ||
      location === 'All' ||
      !locations.find(loc => loc.idx === location)?.addresses[1]
    ) {
      return false;
    }
    return true;
  };

  const getAgents = async () => {
    try {
      const data = await fetchOrgUsers();
      setAgents(data);
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const handleSubmit = () => {
    if (!md) setIsOpen(false);
    setQueryData(prev => ({ ...prev, searchOptions, pageNumber: 0 }));
  };

  const preparedStatuses = React.useMemo(
    () =>
      (
        [
          'ALL',
          'ASSIGNMENT',
          'COMING_SOON',
          'EXCLUSIVE',
          'NEW_CONSTRUCTION',
          'MLS',
        ] as ListingLabel[]
      ).reduce((acc, field) => {
        if (isHidden(field)) return acc;

        return [
          ...acc,
          { value: getDefaultLabel(field), label: getLabel(field) },
        ];
      }, []),
    [getDefaultLabel, getLabel, isHidden],
  );

  const preparedLocations = React.useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...locations.map(currLocation => ({
        label: currLocation.name,
        value: currLocation.idx,
      })),
    ],
    [locations],
  );

  const preparedLocationAddresses = React.useMemo(() => {
    const defaultOption = { value: 'All', label: 'All' };

    if (locations.length === 0 || searchOptions.location === 'All') {
      return [defaultOption];
    }
    const currLocation = locations.find(
      loc => loc.idx === searchOptions.location,
    );
    return [
      defaultOption,
      ...(currLocation?.addresses.map(subLocation => ({
        label: subLocation.address,
        value: subLocation.idx,
      })) || []),
    ];
  }, [locations, searchOptions.location]);

  const preparedAvailabilities = React.useMemo(
    () =>
      ['All', 'Available', 'Unavailable', ...availabilities]
        .filter(avail => !isHidden(avail))
        .map(avail => ({ label: getAvailability(avail), value: avail })),
    [availabilities, getAvailability, isHidden],
  );

  const preparedAgents = React.useMemo(
    () => [
      { value: '', label: 'All' },
      ...agents
        .filter(({ location }) => {
          if (searchOptions.location === 'All') return true;
          return location === searchOptions.location?.toString();
        })
        .sort((a, b) => sortByProperty(a, b, 'label'))
        .map(({ label, id }) => ({ label, value: id })),
    ],
    [agents, locations, searchOptions.location],
  );

  React.useEffect(() => {
    getAvailabilities();
    getAgents();
  }, []);

  React.useEffect(() => {
    if (componentDidMount.current) {
      setSearchOptions(queryData.searchOptions);
      setLocalSearchTerm(queryData.searchTerm);
    } else {
      componentDidMount.current = true;
    }
  }, [queryData.searchOptions, queryData.searchTerm]);

  React.useEffect(() => {
    if (componentDidMount.current) {
      if (debouncedTerm !== queryData.searchTerm) {
        setQueryData(prev => ({ ...prev, searchTerm: debouncedTerm }));
      }
    }
  }, [debouncedTerm]);

  const Wrapper = React.useMemo(() => {
    if (md) return (({ children }) => children) as React.FC<any>;
    return Drawer;
  }, [md]);

  const SubmitButton = () =>
    featureEnabled('supraRebrandingFeature', 1) ? (
      <ButtonV2
        dataCy="submit-button"
        size="small"
        onClick={handleSubmit}
        disabled={loading}
      >
        Apply
      </ButtonV2>
    ) : (
      <Button
        data-cy="submit-button"
        type="primary"
        onClick={handleSubmit}
        disabled={loading}
      >
        Apply
      </Button>
    );

  return (
    <Wrapper
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Filters"
      footer={
        <>
          <SubmitButton />
          <LockButton searchOptions={searchOptions} />
          <Button
            type="text"
            onClick={() => {
              clearSearchOptions();
              setSearchOptions({ ...initialQueryData.searchOptions });
              setLocalSearchTerm('');
            }}
            disabled={loading}
          >
            Clear
          </Button>
        </>
      }
      type="mobile"
      destroyOnClose
    >
      <StyledOptionsSection
        $isOpen={isOpen}
        $transition={transition}
        $height={height}
        $isMobile={!md}
      >
        <div>
          {!md && (
            <SearchBar
              placeholder="Address / MLS"
              value={localSearchTerm}
              onChange={searchTerm => setLocalSearchTerm(searchTerm)}
              shape="round"
            />
          )}
          <InputNumber
            type="currency"
            label="Min Price"
            onChange={(minPrice: number) => handleChange({ minPrice })}
            value={searchOptions.minPrice}
            step={1000}
            fractionDigits={0}
            disabled={loading || filtersLocked}
            data-cy="myBrokerage-input-minPrice"
          />
          <InputNumber
            type="currency"
            label="Max Price"
            onChange={(maxPrice: number) => handleChange({ maxPrice })}
            value={searchOptions.maxPrice}
            step={1000}
            fractionDigits={0}
            disabled={loading || filtersLocked}
            data-cy="myBrokerage-input-maxPrice"
          />
          <Select
            label="Branch"
            onChange={location =>
              handleChange({ location, locationAddress: 'All', agent: '' })
            }
            value={searchOptions.location}
            options={preparedLocations}
            disabled={loading || filtersLocked}
            data-cy="myBrokerage-select-branch"
          />
          {hasSubBrokerage() && (
            <Select
              label="Sub-Brokerage"
              onChange={locationAddress => handleChange({ locationAddress })}
              value={searchOptions.locationAddress}
              options={preparedLocationAddresses}
              disabled={loading || filtersLocked}
            />
          )}
          <Select
            label={getLabel('Status')}
            onChange={status => handleChange({ status })}
            value={searchOptions.status}
            options={preparedStatuses}
            disabled={loading || filtersLocked}
            data-cy="myBrokerage-select-status"
          />
          <Select
            label={getLabel('Availability')}
            onChange={avail => handleChange({ avail })}
            value={searchOptions.avail}
            options={preparedAvailabilities}
            disabled={loading || filtersLocked}
            data-cy="myBrokerage-select-availability"
          />
          <Select
            label="Agent"
            onChange={agent => handleChange({ agent })}
            value={searchOptions.agent}
            options={preparedAgents}
            disabled={loading || filtersLocked}
            showSearch
            filterOption={(input, o) =>
              Boolean(
                o?.label
                  ?.toString()
                  .toUpperCase()
                  .includes(input.toUpperCase()),
              )
            }
            data-cy="myBrokerage-select-agent"
          />
          {md && (
            <StyledDownloadApply>
              <LockButton searchOptions={searchOptions} />
              <PrintButton />
              <SubmitButton />
            </StyledDownloadApply>
          )}
        </div>
      </StyledOptionsSection>
    </Wrapper>
  );
};

export default ListingsSearchOptions;
